import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';
import {
  HeightRecord,
  WeightRecord,
  BmiRecord,
  BloodPressureRecord,
  HeartRateRecord,
  OxygenSaturationRecord,
  GlucoseRecord,
  HealthVitals,
} from '../types';

type VitalType = 'height' | 'weight' | 'bmi' | 'bloodPressure' | 'heartRate' | 'oxygenSaturation' | 'glucose';

// Map vital type to API endpoint
const vitalEndpointMap: Record<VitalType, string> = {
  height: API_ENDPOINTS.healthRecords.height,
  weight: API_ENDPOINTS.healthRecords.weight,
  bmi: API_ENDPOINTS.healthRecords.bmi,
  bloodPressure: API_ENDPOINTS.healthRecords.bloodPressure,
  heartRate: API_ENDPOINTS.healthRecords.heartRate,
  oxygenSaturation: API_ENDPOINTS.healthRecords.oxygenSaturation,
  glucose: API_ENDPOINTS.healthRecords.glucose,
};

const buildConditionBody = (data: ConditionPayload, files?: File[]) => {
  if (!files || files.length === 0) return data;

  const formData = new FormData();
  formData.append('data', JSON.stringify(data));
  files.forEach((file) => formData.append('documents', file));
  return formData;
};

const healthRecordsService = {
  // ============================================
  // Height Records
  // ============================================

  getHeightRecords: async (dependant?: number): Promise<HeightRecord[]> => {
    const params = dependant ? { dependant } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.height, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  getLatestHeight: async (dependant?: number): Promise<HeightRecord | null> => {
    const params = dependant ? { dependant } : {};
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.healthRecords.height}latest/`, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  addHeightRecord: async (data: { value: number; unit: 'cm' | 'ft'; dependant?: number }): Promise<HeightRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.healthRecords.height, data);
    return response.data;
  },

  // ============================================
  // Weight Records
  // ============================================

  getWeightRecords: async (dependant?: number): Promise<WeightRecord[]> => {
    const params = dependant ? { dependant } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.weight, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  getLatestWeight: async (dependant?: number): Promise<WeightRecord | null> => {
    const params = dependant ? { dependant } : {};
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.healthRecords.weight}latest/`, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  addWeightRecord: async (data: { value: number; unit: 'kg' | 'lb'; dependant?: number }): Promise<WeightRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.healthRecords.weight, data);
    return response.data;
  },

  // ============================================
  // BMI Records
  // ============================================

  getBmiRecords: async (dependant?: number): Promise<BmiRecord[]> => {
    const params = dependant ? { dependant } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.bmi, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  getLatestBmi: async (dependant?: number): Promise<BmiRecord | null> => {
    const params = dependant ? { dependant } : {};
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.healthRecords.bmi}latest/`, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  addBmiRecord: async (data: { value: number; unit: string; dependant?: number }): Promise<BmiRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.healthRecords.bmi, data);
    return response.data;
  },

  // ============================================
  // Blood Pressure Records
  // ============================================

  getBloodPressureRecords: async (dependant?: number): Promise<BloodPressureRecord[]> => {
    const params = dependant ? { dependant } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.bloodPressure, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  getLatestBloodPressure: async (dependant?: number): Promise<BloodPressureRecord | null> => {
    const params = dependant ? { dependant } : {};
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.healthRecords.bloodPressure}latest/`, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  addBloodPressureRecord: async (data: {
    systolic: number;
    diastolic: number;
    unit: string;
    type?: string;
    dependant?: number;
  }): Promise<BloodPressureRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.healthRecords.bloodPressure, data);
    return response.data;
  },

  // ============================================
  // Heart Rate Records
  // ============================================

  getHeartRateRecords: async (dependant?: number): Promise<HeartRateRecord[]> => {
    const params = dependant ? { dependant } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.heartRate, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  getLatestHeartRate: async (dependant?: number): Promise<HeartRateRecord | null> => {
    const params = dependant ? { dependant } : {};
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.healthRecords.heartRate}latest/`, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  addHeartRateRecord: async (data: { value: number; unit: string; dependant?: number }): Promise<HeartRateRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.healthRecords.heartRate, data);
    return response.data;
  },

  // ============================================
  // Oxygen Saturation Records
  // ============================================

  getOxygenSaturationRecords: async (dependant?: number): Promise<OxygenSaturationRecord[]> => {
    const params = dependant ? { dependant } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.oxygenSaturation, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  getLatestOxygenSaturation: async (dependant?: number): Promise<OxygenSaturationRecord | null> => {
    const params = dependant ? { dependant } : {};
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.healthRecords.oxygenSaturation}latest/`, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  addOxygenSaturationRecord: async (data: { value: number; unit: string; dependant?: number }): Promise<OxygenSaturationRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.healthRecords.oxygenSaturation, data);
    return response.data;
  },

  // ============================================
  // Glucose Records
  // ============================================

  getGlucoseRecords: async (dependant?: number): Promise<GlucoseRecord[]> => {
    const params = dependant ? { dependant } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.glucose, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  getLatestGlucose: async (dependant?: number): Promise<GlucoseRecord | null> => {
    const params = dependant ? { dependant } : {};
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.healthRecords.glucose}latest/`, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  addGlucoseRecord: async (data: {
    value: number;
    unit: string;
    test_type?: 'fasting' | 'postprandial' | 'random';
    dependant?: number;
  }): Promise<GlucoseRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.healthRecords.glucose, data);
    return response.data;
  },

  // ============================================
  // Generic Methods
  // ============================================

  /**
   * Get all vital records for a specific type
   */
  getVitals: async <T>(type: VitalType, dependant?: number): Promise<T[]> => {
    const endpoint = vitalEndpointMap[type];
    const params = dependant ? { dependant } : {};
    const response = await axiosInstance.get(endpoint, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  /**
   * Get the latest vital record for a specific type
   */
  getLatestVital: async <T>(type: VitalType, dependant?: number): Promise<T | null> => {
    const endpoint = vitalEndpointMap[type];
    const params = dependant ? { dependant } : {};
    try {
      const response = await axiosInstance.get(`${endpoint}latest/`, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  /**
   * Add a new vital record
   */
  addVital: async <T>(type: VitalType, data: Record<string, unknown>): Promise<T> => {
    const endpoint = vitalEndpointMap[type];
    const response = await axiosInstance.post(endpoint, data);
    return response.data;
  },

  deleteVital: async (type: VitalType, id: number): Promise<void> => {
    const endpoint = vitalEndpointMap[type];
    await axiosInstance.delete(`${endpoint}${id}/`);
  },

  /**
   * Get all latest vitals at once (single API call — only returns vitals that have data)
   */
  getAllLatestVitals: async (dependant?: number): Promise<HealthVitals> => {
    const params = dependant ? { dependant } : {};
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.latestAll, { params });
      const data = response.data;
      return {
        height: data.height || null,
        weight: data.weight || null,
        bmi: data.bmi || null,
        bloodPressure: data.bloodPressure || null,
        heartRate: data.heartRate || null,
        oxygenSaturation: data.oxygenSaturation || null,
        glucose: data.glucose || null,
      };
    } catch {
      return {
        height: null, weight: null, bmi: null,
        bloodPressure: null, heartRate: null,
        oxygenSaturation: null, glucose: null,
      };
    }
  },

  /**
   * Get health record choices (units, types, etc.)
   */
  getChoices: async (): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.choices);
    return response.data;
  },

  // ============================================
  // Prescriptions & Lab Tests
  // ============================================

  getPrescriptions: async (params?: {
    doctor_name?: string;
    start_date?: string;
    end_date?: string;
    for_whom?: 'self' | 'dependant';
    dependant?: number;
  }): Promise<PrescriptionRecord[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.prescriptions, { params });
    return response.data.results || response.data;
  },

  getPrescription: async (id: number): Promise<PrescriptionRecord> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.prescriptions}${id}/`);
    return response.data;
  },

  createPrescription: async (data: FormData): Promise<PrescriptionRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.prescriptions, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updatePrescription: async (id: number, data: FormData): Promise<PrescriptionRecord> => {
    const response = await axiosInstance.patch(`${API_ENDPOINTS.prescriptions}${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deletePrescription: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.prescriptions}${id}/`);
  },

  deletePrescriptionDocument: async (recordId: number, documentId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.prescriptions}${recordId}/documents/delete/`, { data: { document_id: documentId } });
  },

  // ============================================
  // Hospitalizations
  // ============================================

  getHospitalizations: async (params?: {
    hospital_name?: string;
    start_date?: string;
    end_date?: string;
    for_whom?: 'self' | 'dependant';
    dependant?: number;
  }): Promise<HospitalizationRecord[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.hospitalizations, { params });
    return response.data.results || response.data;
  },

  getHospitalization: async (id: number): Promise<HospitalizationRecord> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.hospitalizations}${id}/`);
    return response.data;
  },

  createHospitalization: async (data: FormData): Promise<HospitalizationRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.hospitalizations, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updateHospitalization: async (id: number, data: FormData): Promise<HospitalizationRecord> => {
    const response = await axiosInstance.patch(`${API_ENDPOINTS.hospitalizations}${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteHospitalization: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.hospitalizations}${id}/`);
  },

  deleteHospitalizationDocument: async (recordId: number, documentId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.hospitalizations}${recordId}/documents/delete/`, { data: { document_id: documentId } });
  },

  // ============================================
  // Medical Bills
  // ============================================

  getMedicalBills: async (params?: {
    hospital_name?: string;
    start_date?: string;
    end_date?: string;
    for_whom?: 'self' | 'dependant';
    dependant?: number;
  }): Promise<MedicalBillRecord[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.medicalBills, { params });
    return response.data.results || response.data;
  },

  getMedicalBill: async (id: number): Promise<MedicalBillRecord> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.medicalBills}${id}/`);
    return response.data;
  },

  createMedicalBill: async (data: FormData): Promise<MedicalBillRecord> => {
    const response = await axiosInstance.post(API_ENDPOINTS.medicalBills, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updateMedicalBill: async (id: number, data: FormData): Promise<MedicalBillRecord> => {
    const response = await axiosInstance.patch(`${API_ENDPOINTS.medicalBills}${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteMedicalBill: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.medicalBills}${id}/`);
  },

  deleteMedicalBillDocument: async (recordId: number, documentId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.medicalBills}${recordId}/documents/delete/`, { data: { document_id: documentId } });
  },

  // ============================================
  // Vaccination Certificates
  // ============================================

  getVaccinationCertificates: async (params?: {
    vaccine_name?: string;
    start_date?: string;
    end_date?: string;
    for_whom?: 'self' | 'dependant';
    dependant?: number;
  }): Promise<VaccinationCertificate[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.vaccinationCertificates, { params });
    return response.data.results || response.data;
  },

  getVaccinationCertificate: async (id: number): Promise<VaccinationCertificate> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.vaccinationCertificates}${id}/`);
    return response.data;
  },

  createVaccinationCertificate: async (data: FormData): Promise<VaccinationCertificate> => {
    const response = await axiosInstance.post(API_ENDPOINTS.vaccinationCertificates, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updateVaccinationCertificate: async (id: number, data: FormData): Promise<VaccinationCertificate> => {
    const response = await axiosInstance.patch(`${API_ENDPOINTS.vaccinationCertificates}${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteVaccinationCertificate: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.vaccinationCertificates}${id}/`);
  },

  deleteVaccinationDocument: async (recordId: number, documentId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.vaccinationCertificates}${recordId}/documents/delete/`, { data: { document_id: documentId } });
  },

  // Compare 2+ records of the same module. Returns { module, records, differences }.
  compareRecords: async (module: string, recordIds: number[]): Promise<CompareResult> => {
    const response = await axiosInstance.post('/api/health-records/common/compare/', {
      module,
      record_ids: recordIds,
    });
    return response.data;
  },

  getVitalsComparison: async (months = 3): Promise<VitalsComparison> => {
    const response = await axiosInstance.get(API_ENDPOINTS.healthRecords.vitalsComparison, {
      params: { months },
    });
    return response.data;
  },

  // ============================================
  // Health Conditions (self-managed conditions + medicines taken)
  // ============================================

  getHealthConditions: async (params?: {
    status?: 'ongoing' | 'resolved';
    condition_type?: string;
    for_whom?: 'self' | 'dependant';
    dependant?: number;
    search?: string;
    doctor_name?: string;
    start_date_from?: string;
    start_date_to?: string;
    page?: number;
    page_size?: number;
  }): Promise<HealthConditionPage> => {
    const response = await axiosInstance.get(API_ENDPOINTS.healthConditions, { params });
    const data = response.data;
    if (Array.isArray(data)) {
      return { count: data.length, next: null, previous: null, results: data };
    }
    return data;
  },

  getHealthCondition: async (id: number): Promise<HealthConditionRecord> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.healthConditions}${id}/`);
    return response.data;
  },

  createHealthCondition: async (data: ConditionPayload, files?: File[]): Promise<{ message: string; data: HealthConditionRecord }> => {
    const body = buildConditionBody(data, files);
    const response = await axiosInstance.post(
      API_ENDPOINTS.healthConditions,
      body,
      body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    );
    return response.data;
  },

  updateHealthCondition: async (id: number, data: ConditionPayload, files?: File[]): Promise<{ message: string; data: HealthConditionRecord }> => {
    const body = buildConditionBody(data, files);
    const response = await axiosInstance.put(
      `${API_ENDPOINTS.healthConditions}${id}/`,
      body,
      body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    );
    return response.data;
  },

  deleteHealthCondition: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.healthConditions}${id}/`);
  },

  resolveHealthCondition: async (id: number, endDate?: string): Promise<{ message: string; data: HealthConditionRecord }> => {
    const response = await axiosInstance.post(`${API_ENDPOINTS.healthConditions}${id}/resolve/`, {
      end_date: endDate,
    });
    return response.data;
  },

  deleteHealthConditionDocument: async (conditionId: number, documentId: number): Promise<void> => {
    await axiosInstance.delete(
      `${API_ENDPOINTS.healthConditions}${conditionId}/documents/delete/`,
      { data: { document_id: documentId } },
    );
  },

  getHealthConditionChoices: async (): Promise<HealthConditionChoices> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.healthConditions}choices/`);
    return response.data;
  }
};

export interface VitalsComparisonPoint {
  value: number;
  raw: string;
  date: string;
  months_ago: number;
  stale: boolean;
  systolic?: number;
  diastolic?: number;
}

export interface VitalsComparisonRange {
  min: number;
  max: number;
  unit: string;
  display: string;
  source: string;
  source_url: string;
  gender?: string;
  age_range?: string;
}

export interface VitalsComparisonEntry {
  type: 'height' | 'weight' | 'bmi' | 'heartRate' | 'bloodPressure' | 'oxygenSaturation' | 'glucose';
  label: string;
  unit: string;
  normal_range: VitalsComparisonRange | null;
  current: { value: number; raw: string; date: string; systolic?: number; diastolic?: number } | null;
  points: VitalsComparisonPoint[];
  trend: 'up' | 'down' | 'stable';
  direction: 'better' | 'worse' | null;
}

export interface VitalsComparison {
  as_of: string;
  months: number;
  vitals: VitalsComparisonEntry[];
}

export interface CompareResult {
  module: string;
  records: Record<string, any>[];
  differences: Record<string, any[]>;
}

// Types for health records
export interface LabParameter {
  id: number;
  parameter_name: string;
  result: string;
  unit: string;
  start_range?: string;
  end_range?: string;
}

export interface PrescriptionRecord {
  id: number;
  for_whom: 'self' | 'dependant';
  dependant?: number | null;
  dependant_data?: { id: number; name: string; relationship: string; relationship_name?: string } | null;
  record_name: string;
  record_type: string;
  doctor_name: string;
  specialization?: string;
  record_date: string;
  reason?: string;
  parameters?: LabParameter[];
  documents?: { id: number; file: string; created_at: string }[];
  created_at: string;
  updated_at: string;
}

export interface HospitalizationRecord {
  id: number;
  for_whom: 'self' | 'dependant';
  dependant?: number | null;
  dependant_data?: { id: number; name: string; relationship: string; relationship_name?: string } | null;
  hospital_name: string;
  hospitalization_type?: string;
  admission_date: string;
  discharge_date?: string;
  reason?: string;
  doctor_name?: string;
  diagnosis?: string;
  procedure_name?: string;
  room_type?: string;
  total_bill?: number;
  insurance_claimed?: boolean;
  insurance_amount?: number;
  notes?: string;
  documents?: { id: number; file: string; created_at: string }[];
  created_at: string;
  updated_at: string;
}

export interface MedicalBillRecord {
  id: number;
  for_whom: 'self' | 'dependant';
  dependant?: number | null;
  dependant_data?: { id: number; name: string; relationship: string; relationship_name?: string } | null;
  record_name?: string;
  hospital_name: string;
  bill_date: string;
  record_bill_number?: string;
  bill_type: string;
  amount: number;
  paid_amount?: number;
  insurance_claimed?: boolean;
  insurance_amount?: number;
  notes?: string;
  documents?: { id: number; file: string; created_at: string }[];
  created_at: string;
  updated_at: string;
}

export interface VaccinationCertificate {
  id: number;
  for_whom: 'self' | 'dependant';
  dependant?: number | null;
  dependant_data?: { id: number; name: string; relationship: string; relationship_name?: string } | null;
  vaccine_name: string;
  vaccination_date: string;
  dose_number?: number;
  total_doses?: number;
  administered_by?: string;
  location?: string;
  batch_number?: string;
  next_due_date?: string;
  registration_id?: string;
  notes?: string;
  documents?: { id: number; file: string; created_at: string }[];
  created_at: string;
  updated_at: string;
}

export interface MedicationTaken {
  id?: number;
  medicine_name: string;
  medicine_type: string;
  medicine_type_display?: string;
  dosage?: string;
  frequency: string;
  frequency_display?: string;
  start_date?: string | null;
  end_date?: string | null;
  still_taking: boolean;
  source: string;
  source_display?: string;
  is_self_prescribed: boolean;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface HealthConditionPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: HealthConditionRecord[];
}

export interface HealthConditionRecord {
  id: number;
  for_whom: 'self' | 'dependant';
  dependant?: number | null;
  dependant_data?: { id: number; name: string; relationship?: string; relationship_name?: string } | null;
  condition_name: string;
  condition_type: string;
  condition_type_display?: string;
  severity: 'mild' | 'moderate' | 'severe';
  severity_display?: string;
  start_date: string;
  end_date?: string | null;
  status: 'ongoing' | 'resolved';
  status_display?: string;
  is_ongoing?: boolean;
  duration_days?: number | null;
  treated_by_doctor: boolean;
  doctor_name?: string | null;
  notes?: string;
  medications?: MedicationTaken[];
  medication_count?: number;
  documents?: { id: number; file: string; created_at?: string }[];
  created_at?: string;
  updated_at?: string;
}

export interface ConditionPayload {
  condition_name: string;
  condition_type?: string;
  severity?: 'mild' | 'moderate' | 'severe';
  start_date: string;
  end_date?: string | null;
  status?: 'ongoing' | 'resolved';
  treated_by_doctor?: boolean;
  doctor_name?: string | null;
  notes?: string;
  for_whom?: 'self' | 'dependant';
  dependant?: number | null;
  medications?: Partial<MedicationTaken>[];
  keep_medications?: number[];
}

export interface ChoiceOption {
  value: string;
  label: string;
}

export interface HealthConditionChoices {
  condition_types: ChoiceOption[];
  statuses: ChoiceOption[];
  severities: ChoiceOption[];
  medicine_types: ChoiceOption[];
  frequencies: ChoiceOption[];
  medication_sources: ChoiceOption[];
}

export default healthRecordsService;
